import { useMemo, useState } from "react";
import type { LoadedCatalog } from "../catalog.ts";
import type { Settings } from "../state/store.ts";
import type { ExamSession, ExamResult, ExamQuestionState } from "../exam.ts";
import { isCorrect } from "../exam.ts";
import { makeTopicResolver } from "../catalog.ts";
import { t } from "../i18n/ui.ts";
import { QuestionCard } from "./QuestionCard.tsx";

interface Props {
  catalog: LoadedCatalog;
  settings: Settings;
  session: ExamSession;
  result: ExamResult;
  onNewExam: () => void;
  onExit: () => void;
}

type Filter = "wrong" | "all";

export function ResultsView({
  catalog, settings, session, result, onNewExam, onExit,
}: Props) {
  const lang = settings.primary;
  const [filter, setFilter] = useState<Filter>("wrong");

  const byCategory = useMemo(
    () => summariseByCategory(catalog, session, lang),
    [catalog, session, lang]
  );

  const shown =
    filter === "wrong" ? result.wrong : session.items;

  const minutes = Math.floor(result.durationMs / 60000);
  const seconds = Math.floor((result.durationMs % 60000) / 1000);

  return (
    <div className="results">
      <section
        className={`verdict verdict--${result.passed ? "pass" : "fail"}`}
        aria-labelledby="verdict-title"
      >
        <h2 id="verdict-title" className="verdict__title">
          {t(lang, result.passed ? "passed" : "failed")}
        </h2>

        <dl className="verdict__stats">
          <div>
            <dt>{t(lang, "errorPoints")}</dt>
            <dd className="verdict__big">
              {result.errorPoints}
              <span className="verdict__max"> / {result.maxPoints}</span>
            </dd>
          </div>
          <div>
            <dt>{t(lang, "correct")}</dt>
            <dd className="verdict__big">
              {result.correctCount}
              <span className="verdict__max"> / {result.total}</span>
            </dd>
          </div>
          <div>
            <dt>{t(lang, "timeTaken")}</dt>
            <dd className="verdict__big">
              {minutes}:{String(seconds).padStart(2, "0")}
            </dd>
          </div>
        </dl>

        <ul className="verdict__reasons">
          {result.failedOnPoints && (
            <li>{t(lang, "failReasonPoints", { max: result.maxPoints })}</li>
          )}
          {result.failedOnFivePointRule && <li>{t(lang, "failReasonTwoFive")}</li>}
          {result.passed && <li>{t(lang, "passRule", { max: result.maxPoints })}</li>}
        </ul>

        <div className="verdict__actions">
          <button type="button" className="btn btn--primary" onClick={onNewExam}>
            {t(lang, "newExam")}
          </button>
          <button type="button" className="btn" onClick={onExit}>
            {t(lang, "home")}
          </button>
        </div>
      </section>

      {byCategory.length > 0 && (
        <section className="breakdown" aria-labelledby="breakdown-title">
          <h3 id="breakdown-title">{t(lang, "byCategory")}</h3>
          <ul className="breakdown__list">
            {byCategory.map((row) => (
              <li key={row.id} className="breakdown__row">
                <span className="breakdown__label">{row.title}</span>
                <span
                  className="breakdown__bar"
                  role="img"
                  aria-label={t(lang, "scoreCorrect", {
                    correct: row.correct, total: row.total,
                  })}
                >
                  <span
                    className="breakdown__fill"
                    style={{ inlineSize: `${(row.correct / row.total) * 100}%` }}
                  />
                </span>
                <span className="breakdown__count">
                  {row.correct}/{row.total}
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}

      <section className="review" aria-labelledby="review-title">
        <div className="review__head">
          <h3 id="review-title">
            {t(lang, filter === "wrong" ? "reviewMistakes" : "reviewAll")}
          </h3>
          <div className="segmented" role="group">
            <button
              type="button"
              className={`segmented__btn ${filter === "wrong" ? "is-active" : ""}`}
              aria-pressed={filter === "wrong"}
              onClick={() => setFilter("wrong")}
            >
              {t(lang, "reviewMistakes")} ({result.wrong.length})
            </button>
            <button
              type="button"
              className={`segmented__btn ${filter === "all" ? "is-active" : ""}`}
              aria-pressed={filter === "all"}
              onClick={() => setFilter("all")}
            >
              {t(lang, "reviewAll")} ({session.items.length})
            </button>
          </div>
        </div>

        {shown.length === 0 ? (
          <p className="review__empty">{t(lang, "noResults")}</p>
        ) : (
          <ol className="review__list">
            {shown.map((item) => (
              <li key={item.question.id}>
                <div
                  className={`review__item review__item--${
                    isCorrect(item) ? "ok" : "bad"
                  }`}
                >
                  <p className="review__badge">
                    {t(lang, isCorrect(item) ? "correct" : "incorrect")}
                  </p>
                  <QuestionCard
                    question={item.question}
                    settings={settings}
                    selected={item.selected}
                    typed={item.typed}
                    onToggle={() => {}}
                    onType={() => {}}
                    revealed
                    showExplanation
                    // Reviewing, not answering — both languages are useful here.
                    showSecondary
                    disabled
                  />
                </div>
              </li>
            ))}
          </ol>
        )}
      </section>
    </div>
  );
}

interface CategoryRow {
  id: number;
  title: string;
  correct: number;
  total: number;
}

/**
 * Groups the sheet's questions under their top-level topic so the summary shows
 * where the mistakes clustered rather than just a total.
 */
function summariseByCategory(
  catalog: LoadedCatalog,
  session: ExamSession,
  lang: Settings["primary"]
): CategoryRow[] {
  const topicOf = makeTopicResolver(catalog);

  const rows = new Map<string, CategoryRow>();
  for (const item of session.items) {
    const topic = item.question.categoryIds.map(topicOf).find((t) => t != null);
    if (topic == null) continue;
    const title = catalog.categoryTitle(topic, lang);
    let row = rows.get(title);
    if (!row) {
      row = { id: topic, title, correct: 0, total: 0 };
      rows.set(title, row);
    }
    row.total++;
    if (isCorrect(item)) row.correct++;
  }

  return [...rows.values()].sort(
    (a, b) => a.correct / a.total - b.correct / b.total || b.total - a.total
  );
}
